"use client";

import { useRef } from "react";
import type { ReactNode } from "react";
import Eyebrow from "./Eyebrow";
import WipTag from "./WipTag";
import { useMaskedReveal } from "./motion";

interface SectionHeadProps {
  eyebrow: string;
  /** Optional roadmap tag shown next to the eyebrow. */
  wip?: { label: string; variant?: "amber" | "blue" | "green" };
  /** Pass JSX so the caller marks the gradient span via <GradText>. */
  title: ReactNode;
  lead?: ReactNode;
  center?: boolean;
  /** `light` = light-on-dark styling for drenched bands; `pink` = the pink
   *  drenched band (white text, white eyebrow — accent has no contrast there). */
  tone?: "default" | "light" | "pink";
  /** Clearing fill for this head's grid block (e.g. DARK_BAND inside one). */
  gridFill?: string;
}

/**
 * Eyebrow (+ optional roadmap tag) → title → lead. Shared by all sections.
 *
 * Self-choreographed on scroll enter: the title reveals as masked SplitText
 * lines rising out of their own line-box, eyebrow and lead settle in around
 * it. Runs once, after fonts are ready (so line breaks are final), and not at
 * all under prefers-reduced-motion. Callers must NOT wrap this in another
 * reveal (no AnimateOnScroll) — it would double-animate.
 */
export default function SectionHead({
  eyebrow,
  wip,
  title,
  lead,
  center = false,
  tone = "default",
  gridFill,
}: SectionHeadProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  useMaskedReveal(rootRef, { heading: "h2" });

  return (
    <div
      ref={rootRef}
      data-grid-block
      data-grid-fill={gridFill}
      className={`mb-[54px] max-w-[700px] ${center ? "mx-auto text-center" : ""}`}
    >
      <div
        data-head-sub
        className={`flex flex-wrap items-center gap-2.5 ${center ? "justify-center" : ""}`}
      >
        <Eyebrow tone={tone}>{eyebrow}</Eyebrow>
        {wip && <WipTag label={wip.label} variant={wip.variant} />}
      </div>
      <h2
        className={`mt-5 text-[clamp(30px,4vw,48px)] font-bold leading-[1.06] tracking-[-0.027em] ${
          tone !== "default" ? "text-white" : ""
        }`}
      >
        {title}
      </h2>
      {lead && (
        <p
          data-head-sub
          className={`mt-[18px] text-[clamp(16px,1.4vw,19px)] leading-[1.6] ${
            tone !== "default" ? "text-white/75" : "text-muted"
          } ${center ? "mx-auto" : ""}`}
        >
          {lead}
        </p>
      )}
    </div>
  );
}
